import { FC, useState, Suspense, useEffect } from 'react';
import { PreloadedQuery, useLazyLoadQuery, useQueryLoader } from 'react-relay';
import { getSkillsByTarget, userSuggestedSkills } from '../../graphQLOperations/Dashboard';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectedTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { setFilter } from '../../redux/reducers/filterSlice';
import { setStudentSkills } from '../../redux/reducers/studentSkillsSlice';
import { selectedStudentTour } from '../../redux/reducers/studentTourSlice';
import { selectedSuggestedView } from '../../redux/reducers/suggestedViewSlice';
import { selectedUnit } from '../../redux/reducers/unitSlice';
import DashboardContainerBody from './DashboardContainerBody';
import { StudentTour } from '../../models/StudentTour';
import { StudentSkillStates } from '../../models/StudentSkillState';
import { filtersInitialState } from '../../models/DashboardSkillsFilter';
import { Unit } from '../../models/Unit';
import { CardsMessages } from '../../models/CardsMessages';
import { filterAndSliceSuggested } from '../../businessLogicService/filterAndSliceSuggested';
import { updateSSSWithIcons } from '../../businessLogicService/addIconsToCards';
import { DashboardUserSuggestedSkillsQuery } from '../../graphQLOperations/__generated__/DashboardUserSuggestedSkillsQuery.graphql';
import { selectUser } from '../../redux/reducers/userSlice';
import { sortStudentSkillStatesByLabel } from '../../businessLogicService/sortCriteria';
import { DashboardSkillsByTargetQuery } from '../../graphQLOperations/__generated__/DashboardSkillsByTargetQuery.graphql';
import { setFilterCount } from '../../redux/reducers/filterCountSlice';
import { DashboardGetStudentExitSlipQuery } from '../../graphQLOperations/__generated__/DashboardGetStudentExitSlipQuery.graphql';
import { useStudentSkillStates } from '../../custom-hooks/useStudentSkillStates';

interface Props {
  studentSkillStates: StudentSkillStates[];
  firstRender: boolean;
  setFirstRender: (f: boolean) => void;
  queryReferenceExitSlip: PreloadedQuery<DashboardGetStudentExitSlipQuery>;
}

const DashboardList: FC<Props> = ({ studentSkillStates, firstRender, setFirstRender, queryReferenceExitSlip }) => {
  const dispatch = useAppDispatch();
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const userRedux = useAppSelector(selectUser);
  const unitRedux = useAppSelector(selectedUnit);
  const tourMode = useAppSelector(selectedStudentTour);
  const suggestedViewMode = useAppSelector(selectedSuggestedView);
  const [customCardMessage, setCustomCardMessage] = useState<string>(CardsMessages.DEFAULT);
  const [queryReferenceSkillsByTarget] = useQueryLoader<DashboardSkillsByTargetQuery>(getSkillsByTarget);
  const fetchSuggestedSkills = Boolean(unitRedux?.id);

  const { studentSkillStates: sssForUnitMode } = useStudentSkillStates({
    unitToFilterBy: unitRedux?.id,
    includeExtensions: false,
    filterByTargetGroup: true,
  });

  useEffect(() => {
    if (sssForUnitMode.length > 0) {
      dispatch(setStudentSkills(sssForUnitMode));
    }
  }, [sssForUnitMode, dispatch]);

  const { studentSuggestedSkills } = useLazyLoadQuery<DashboardUserSuggestedSkillsQuery>(
    userSuggestedSkills,
    {
      skip: !fetchSuggestedSkills,
      studentId: 'StudentNode:' + userRedux?.id,
      targetId: 'TargetNode:' + unitRedux?.id,
    },
    {
      fetchPolicy: fetchSuggestedSkills ? 'store-or-network' : 'store-only',
    },
  );

  useEffect(() => {
    if (!firstRender) dispatch(setFilter(filtersInitialState));
    dispatch(setFilterCount(0));
  }, [tourMode, suggestedViewMode]);

  useEffect(() => {
    setFirstRender(false);
  }, []);

  useEffect(() => {
    if (targetGroupRedux && studentSkillStates) {
      setCustomCardMessage(CardsMessages.DEFAULT);
      studentSkillStates = sortStudentSkillStatesByLabel(studentSkillStates, 'readinessScore');

      studentSkillStates = updateSSSWithIcons(
        studentSkillStates,
        tourMode as StudentTour,
        targetGroupRedux,
        unitRedux as Unit,
      );

      if (!suggestedViewMode && unitRedux) {
        if (studentSkillStates.length !== 0) {
          const { element, cardMessage } = filterAndSliceSuggested(studentSkillStates, tourMode as StudentTour);
          studentSkillStates = element;
          setCustomCardMessage(cardMessage);
        } else if (tourMode === StudentTour.UNITS && studentSuggestedSkills !== null) {
          const selectedIds = studentSuggestedSkills?.map(({ id }) => id);
          studentSkillStates = studentSkillStates.filter(obj => selectedIds?.includes(obj.id));
          const { cardMessage } = filterAndSliceSuggested(studentSkillStates, tourMode as StudentTour);
          setCustomCardMessage(cardMessage);
        }
      }

      if (!suggestedViewMode) dispatch(setStudentSkills(studentSkillStates));
    }
  }, [studentSkillStates, tourMode, suggestedViewMode, unitRedux]);

  return (
    // `tw-isolate` gives the dashboard its own stacking context so the connectors'/badges' z-index
    // (z-10/z-20) stay scoped here and no longer paint over the fixed app header (nc_header, z-index 4).
    <div className="tw-isolate tw-flex tw-flex-col tw-justify-center tw-mx-auto tw-w-10/12 !tw-bg-none !tw-border-none dashboard-student-list">
      <Suspense>
        <DashboardContainerBody
          customCardMessage={customCardMessage}
          queryReferenceSkillsByTarget={queryReferenceSkillsByTarget as PreloadedQuery<DashboardSkillsByTargetQuery>}
          queryReferenceExitSlip={queryReferenceExitSlip as PreloadedQuery<DashboardGetStudentExitSlipQuery>}
        />
      </Suspense>
    </div>
  );
};

export default DashboardList;
